import { HierarchyNode } from 'core/API/hierarchy';
import { isHiddenSecurityRole } from 'module/settings/settingsGameMasterTabs/GeneralSetting/constants';
import {
	isUserIncludedInWorkplaceUserList,
	WorkplaceUserListScopeOptions,
} from 'module/settings/settingsGameMasterTabs/GeneralSetting/UserSettings/filterUserSettingsUsers';

export interface FilterSelectableWorkplaceUsersOptions extends WorkplaceUserListScopeOptions {
	workplaceId?: string | number;
	hierarchyNodes?: HierarchyNode[];
	/** When true, applies workplace hierarchy scoping. */
	applyWorkplaceScopeFilter?: boolean;
}

/** System role names hidden from all user-visible lists (in addition to HIDDEN_SECURITY_ROLES). */
const HIDDEN_SYSTEM_ROLE_NAMES: readonly string[] = ['Admin User', 'Support User'];

/** BYOD system accounts hidden from all user pickers and lists. */
const HIDDEN_BYOD_SYSTEM_EMAILS = new Set(['admin@byod.ai', 'support@byod.ai']);

export const getUserSecurityRoleName = (user: any): string =>
	user?.securityRole?.name ||
	user?._raw?.securityRole?.name ||
	user?._raw?.security_role?.name ||
	user?._full?.securityRole?.name ||
	'';

/**
 * Returns true when a user should be excluded from workplace user pickers and lists.
 */
export const isHiddenFromUserLists = (user: any): boolean => {
	if (!user) return true;

	const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
	const firstName = (user.firstName || '').toLowerCase();
	const lastName = (user.lastName || '').toLowerCase();
	const email = (user.email || '').toLowerCase();
	const normalizedEmail = (user.email || '').trim().toLowerCase();
	const roleName = getUserSecurityRoleName(user);

	if (
		roleName &&
		(isHiddenSecurityRole(roleName) || HIDDEN_SYSTEM_ROLE_NAMES.includes(roleName))
	) {
		return true;
	}

	const isAdminUser =
		fullName.includes('admin user') ||
		fullName === 'admin user' ||
		(firstName === 'admin' && lastName === 'user');

	const isSupportUser =
		fullName.includes('support user') ||
		fullName === 'support user' ||
		(firstName === 'support' && lastName === 'user');

	const hasAdminSupportEmail =
		email.includes('admin@') ||
		email.includes('support@') ||
		email.includes('@admin') ||
		email.includes('@support');

	const isHiddenUser =
		fullName.includes('hidden') ||
		email.includes('hidden') ||
		firstName === 'hidden' ||
		user._raw?.hidden === true ||
		user._raw?.isHidden === true;

	const isSeparated = user.separation === true || user._raw?.separation === true;

	const isByodSystemEmail = HIDDEN_BYOD_SYSTEM_EMAILS.has(normalizedEmail);

	return (
		isAdminUser ||
		isSupportUser ||
		hasAdminSupportEmail ||
		isByodSystemEmail ||
		isHiddenUser ||
		isSeparated
	);
};

const normalizeWorkplaceId = (workplaceId: string | number): number => {
	const parsed = Number(workplaceId);
	return Number.isNaN(parsed) ? 0 : parsed;
};

/** Canonical filter for all workplace user dropdowns and pickers. */
export const filterSelectableWorkplaceUsers = <T>(
	users: T[],
	options?: FilterSelectableWorkplaceUsersOptions
): T[] => {
	const applyWorkplaceScopeFilter = options?.applyWorkplaceScopeFilter ?? false;
	const workplaceId = options?.workplaceId;
	const hierarchyNodes = options?.hierarchyNodes ?? [];
	const includeAboveWorkplaceUsers = options?.includeAboveWorkplaceUsers ?? false;
	const currentUserHierarchyId = options?.currentUserHierarchyId ?? null;

	return users.filter((user) => {
		if (isHiddenFromUserLists(user)) return false;

		if (!applyWorkplaceScopeFilter || workplaceId == null) {
			return true;
		}

		return isUserIncludedInWorkplaceUserList(
			user as Parameters<typeof isUserIncludedInWorkplaceUserList>[0],
			normalizeWorkplaceId(workplaceId),
			hierarchyNodes,
			{ includeAboveWorkplaceUsers, currentUserHierarchyId }
		);
	});
};

/** Visibility-only filter (no workplace hierarchy scoping). Used by User Settings fetch paths. */
export const filterVisibleWorkplaceUsers = <T>(users: T[]): T[] =>
	filterSelectableWorkplaceUsers(users);
